"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const industries = [
  {
    href: "/industries/retail",
    icon: "🏪",
    title: "Retail",
    desc: "POS systems, multi-site networks, EFTPOS connectivity, and inventory system integration for retail businesses.",
    tags: ["POS Systems","Multi-Site Networks","EFTPOS","Inventory Systems"],
  },
  {
    href: "/industries/travel",
    icon: "✈️",
    title: "Travel",
    desc: "Distributed office infrastructure, 24/7 system reliability, cloud collaboration, and secure client booking data.",
    tags: ["Distributed Offices","24/7 Reliability","Cloud Systems","Secure Client Data"],
  },
  {
    href: "/industries/professional-services",
    icon: "💼",
    title: "Professional Services",
    desc: "Secure document management, Microsoft 365 productivity, compliance, and remote work infrastructure.",
    tags: ["Document Security","M365","Compliance","Remote Work"],
  },
  {
    href: "/industries/construction",
    icon: "🏗️",
    title: "Construction",
    desc: "Site connectivity, mobile workforce management, project management tools, and multi-location operations.",
    tags: ["Site Connectivity","Mobile Workers","Project Management","Multi-Location"],
  },
  {
    href: "/industries/healthcare",
    icon: "🏥",
    title: "Healthcare / Clinics",
    desc: "Secure patient data management, compliance requirements, reliable systems, and VOIP communications.",
    tags: ["Patient Data Security","Compliance","Reliable Systems","VOIP"],
  },
  {
    href: "/industries/property-development",
    icon: "🏠",
    title: "Property Development & Real Estate",
    desc: "Multi-site networking, secure document sharing, show home Wi-Fi, and rapid site office deployment.",
    tags: ["Multi-Site Networks","Document Security","Show Home Wi-Fi","Site Offices"],
  },
];

export default function IndustriesPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Industries" },
          ]}
        />
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 50%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Industries We Serve</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                IT Solutions Built for<br />
                <span className="gradient-text">Your Industry</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-3xl mx-auto" style={{ color: "#94A3B8" }}>
                We understand the unique technology challenges different industries face. Our solutions are tailored to your specific operational requirements.
              </p>
            </FadeInView>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.1}>
              {industries?.map((industry) => (
                <Link key={industry?.href} href={industry?.href}
                  className="group rounded-card-lg p-5 sm:p-8 transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/30 h-full flex flex-col"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="text-4xl mb-4">{industry?.icon}</div>
                  <h2 className="font-heading font-700 text-[22px] mb-3" style={{ color: "#F8FAFC" }}>{industry?.title}</h2>
                  <p className="text-[14px] font-body mb-5 leading-[1.75]" style={{ color: "#94A3B8" }}>{industry?.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {industry?.tags?.map(tag => (
                      <span key={tag} className="px-2.5 py-1 rounded-lg text-[11px] font-body" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(203,213,225,0.7)" }}>{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>
                    Learn more
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </div>
                </Link>
              ))}
            </StaggerContainer>

          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Don&apos;t see your industry?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>We work with businesses across many sectors. Get in touch to discuss your specific requirements.</p>
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Talk to Our Team</Link>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
